import { useState, useEffect, useMemo, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { BaseComponent } from '../../types/protocols';
import { getBuildComponentTypeTranslation } from '../../Helpers/BuildComponentTypesStore';

// ── Types ───────────────────────────────────────────────────────────

export interface FieldChange {
    field: string;
    oldValue: string | null;
    newValue: string | null;
    changedBy: string | null;
}

interface ArchiveDiff {
    changes: FieldChange[];
}

// ValueWithMetadata structure for detecting new format
interface ValueWithMetadataShape<T> {
    value: T | null;
    status?: string;
    updatedBy?: string;
    updatedWhen?: string;
}

// Check if a value is a ValueWithMetadata object (has 'value' key)
function isValueWithMetadata<T>(val: unknown): val is ValueWithMetadataShape<T> {
    return typeof val === 'object' && val !== null && 'value' in val;
}

// Check if a value is an "unset" ValueWithMetadata (has 'status' but no 'value')
function isUnsetValueWithMetadata(val: unknown): boolean {
    return typeof val === 'object' && val !== null && 'status' in val && !('value' in val);
}

/**
 * Fallback attribution for diff rows stored without changedBy.
 * The archive entry keeps the full entity snapshot alongside `_diff`, and a
 * ValueWithMetadata field in that snapshot still carries the `updatedBy` of
 * whoever made the archived change (older trigger versions failed to extract
 * it for cleared fields, which are stored without a `value` key).
 *
 * Cannot recover rows where the field is ABSENT from the snapshot — fields
 * cleared by the old web code's remove(), and plain non-VWM fields, hold no
 * UID; those rows keep rendering "Okänd användare".
 */
export function extractSnapshotUpdatedBy(field: unknown): string | null {
    if (!isValueWithMetadata(field) && !isUnsetValueWithMetadata(field)) return null;
    const updatedBy = (field as { updatedBy?: unknown }).updatedBy;
    return typeof updatedBy === 'string' && updatedBy !== '' ? updatedBy : null;
}

// Extract the raw value from either plain value or ValueWithMetadata
function extractValue<T>(val: T | ValueWithMetadataShape<T> | undefined): T | null {
    if (val === undefined || val === null) return null;
    // Handle "unset" ValueWithMetadata like { status: "notSet" } - treat as null
    if (isUnsetValueWithMetadata(val)) {
        return null;
    }
    if (isValueWithMetadata<T>(val)) {
        return val.value;
    }
    return val as T;
}

interface ComponentArchiveEntry extends Omit<Partial<BaseComponent>, 'specificType' | 'label'> {
    _archiveMetadata: {
        archivedAt: string;
        archivedDate: string;
        buildingKey: string;
        componentKey: string;
    };
    _diff?: ArchiveDiff;
    // These can be either plain strings (old data) or ValueWithMetadata (new data)
    label?: string | ValueWithMetadataShape<string>;
    specificType?: string | ValueWithMetadataShape<string>;
}

interface LevelArchiveEntry {
    _archiveMetadata: {
        archivedAt: string;
        archivedDate: string;
        buildingKey: string;
        levelKey: string;
        deleted?: boolean;
    };
    _diff?: ArchiveDiff;
    name?: string;
    number?: number;
    type?: string;
    ceilingHeight?: number;
    planImage?: unknown;
    blueprints?: Record<string, unknown>;
    currentBlueprintKey?: string;
}

interface RoomArchiveEntry {
    _archiveMetadata: {
        archivedAt: string;
        archivedDate: string;
        buildingKey: string;
        roomKey: string;
        deleted?: boolean;
    };
    _diff?: ArchiveDiff;
    label?: string | ValueWithMetadataShape<string>;
    number?: string;
    type?: string;
    levelKey?: string;
    ceilingHeight?: ValueWithMetadataShape<number>;
    electricalInstallation?: ValueWithMetadataShape<unknown>;
    airTerminalInstallation?: ValueWithMetadataShape<unknown>;
    radiatorInstallation?: ValueWithMetadataShape<unknown>;
    photos?: Record<string, unknown>;
    blueprintLocations?: Record<string, unknown>;
    workEnvironmentRisk?: unknown;
}

type ArchiveEntry = ComponentArchiveEntry | LevelArchiveEntry | RoomArchiveEntry;

export interface HistoryEntry {
    archiveKey: string;
    archivedAt: string;            // "YYYY-MM-DD HH:mm:ss"
    entityType: EntityType;
    entityKey: string;             // componentKey, levelKey, or roomKey
    entityName: string;            // Display name for the entity
    changes: FieldChange[];
    // Keep for backwards compatibility
    componentKey: string;
    componentName: string;
}

export interface HistoryDateGroup {
    groupKey: string;              // "YYYY-MM-DD"
    dateKey: string;               // "YYYY-MM-DD"
    userUids: string[];            // unique non-null UIDs from entries
    entries: HistoryEntry[];       // sorted by archivedAt descending
}

// ── Field configuration (single source of truth) ────────────────────

/** Swedish translations for tracked component fields.
 *  Also serves as the canonical set of relevant fields for filtering. */
export const FIELD_TRANSLATIONS: { [key: string]: string } = {
    // Component fields
    condition: 'Skick',
    material: 'Material',
    color: 'Färg',
    comment: 'Kommentar',
    cardinalDirection: 'Väderstreck',
    roofShape: 'Takform',
    roofSlope: 'Taklutning',
    depth: 'Djup',
    insulation: 'Isolering',
    buildingRisks: 'Byggrisker',
    hazardous: 'Farliga ämnen',
    elevatorType: 'Hisstyp',
    warrantyStartDate: 'Garantistart',
    warrantyDurationYears: 'Garantitid (år)',
    documents: 'Dokument',
    specificType: 'Komponenttyp',
    utilityCategory: 'Kategori',
    outdoorCategory: 'Kategori',
    label: 'Namn',
    brand: 'Märke',
    photos: 'Foton',
    blueprintLocations: 'Ritningsplacering',
    mapLocations: 'Kartplacering',
    parentRoomKeys: 'Rumstillhörighet',
    areaShapes: 'Ytformer',
    // RoofSafety fields
    safetyStatus: 'Säkerhetsstatus',
    anchorPoints: 'Fästpunkter',
    accessInfo: 'Accessinformation',
    accessPositions: 'Takaccesspunkter',
    // Level fields
    name: 'Namn',
    number: 'Nummer',
    ceilingHeight: 'Takhöjd',
    type: 'Typ',
    planImage: 'Planritning',
    blueprints: 'Ritningar',
    currentBlueprintKey: 'Aktiv ritning',
    // Room fields
    levelKey: 'Våning',
    levelKeys: 'Våningar',
    electricalInstallation: 'Elinstallation',
    airTerminalInstallation: 'Ventilation',
    radiatorInstallation: 'Uppvärmning',
    workEnvironmentRisk: 'Arbetsmiljörisk',
    // Component replacement fields
    replacedByKey: 'Ersatt av',
    replacedFromKey: 'Ersatte',
};

/** Entity types that can be tracked in the archive */
export type EntityType = 'components' | 'levels' | 'rooms';

const RELEVANT_FIELDS = new Set(Object.keys(FIELD_TRANSLATIONS));

// ── Shared Helpers (exported for use in UI components) ──────────────

export type ChangeAction = 'added' | 'changed' | 'removed';

export function translateFieldName(field: string): string {
    return FIELD_TRANSLATIONS[field] || field;
}

export function classifyChange(change: FieldChange): ChangeAction {
    const hasOld = change.oldValue != null && change.oldValue !== '';
    const hasNew = change.newValue != null && change.newValue !== '';
    if (!hasOld && hasNew) return 'added';
    if (hasOld && !hasNew) return 'removed';
    return 'changed';
}

export const ACTION_LABELS: Record<ChangeAction, string> = {
    added: 'Lade till',
    changed: 'Ändrade',
    removed: 'Tog bort',
};

/** Default number of history groups to show in collapsed views */
export const DEFAULT_HISTORY_LIMIT = 5;

// ── Internal Helpers ─────────────────────────────────────────────────

function isComponentArchiveEntry(entry: ArchiveEntry): entry is ComponentArchiveEntry {
    return 'componentKey' in entry._archiveMetadata;
}

function isLevelArchiveEntry(entry: ArchiveEntry): entry is LevelArchiveEntry {
    return 'levelKey' in entry._archiveMetadata;
}

function isRoomArchiveEntry(entry: ArchiveEntry): entry is RoomArchiveEntry {
    return 'roomKey' in entry._archiveMetadata;
}

function getEntityDisplayName(entry: ArchiveEntry): string {
    if (isComponentArchiveEntry(entry)) {
        // Handle both old (plain string) and new (ValueWithMetadata) formats
        const labelValue = extractValue(entry.label);
        if (labelValue) return labelValue;

        const specificTypeValue = extractValue(entry.specificType);
        if (specificTypeValue) {
            return getBuildComponentTypeTranslation(specificTypeValue, entry.type);
        }
        return entry._archiveMetadata.componentKey;
    }

    if (isLevelArchiveEntry(entry)) {
        if (entry.name) return entry.name;
        if (entry.number !== undefined) return `Våning ${entry.number}`;
        return entry._archiveMetadata.levelKey;
    }

    if (isRoomArchiveEntry(entry)) {
        const labelValue = extractValue(entry.label);
        if (labelValue) return labelValue;
        if (entry.number) return `Rum ${entry.number}`;
        return entry._archiveMetadata.roomKey;
    }

    return 'Okänd';
}

function getEntityKey(entry: ArchiveEntry): string {
    if (isComponentArchiveEntry(entry)) return entry._archiveMetadata.componentKey;
    if (isLevelArchiveEntry(entry)) return entry._archiveMetadata.levelKey;
    if (isRoomArchiveEntry(entry)) return entry._archiveMetadata.roomKey;
    return '';
}

function getEntityType(entry: ArchiveEntry): EntityType {
    if (isComponentArchiveEntry(entry)) return 'components';
    if (isLevelArchiveEntry(entry)) return 'levels';
    if (isRoomArchiveEntry(entry)) return 'rooms';
    return 'components';
}

// Keep for backwards compatibility
function getComponentDisplayName(entry: ArchiveEntry): string {
    return getEntityDisplayName(entry);
}

export function formatSwedishDate(dateKey: string): string {
    // dateKey is "YYYY-MM-DD"
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('sv-SE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function extractDateKey(archivedAt: string): string {
    return archivedAt.substring(0, 10); // "YYYY-MM-DD"
}

function collectUserUids(entries: HistoryEntry[]): string[] {
    const uids = new Set<string>();
    for (const entry of entries) {
        for (const change of entry.changes) {
            if (change.changedBy) uids.add(change.changedBy);
        }
    }
    return Array.from(uids);
}

// ── Hook ────────────────────────────────────────────────────────────

export function useArchiveHistory(
    buildingKey: string | null | undefined,
    entityKey?: string | null,
    entityTypes?: EntityType | EntityType[],
) {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().substring(0, 10);
    });
    const [endDate, setEndDate] = useState(() => {
        return new Date().toISOString().substring(0, 10);
    });
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Normalize entityTypes to an array
    const normalizedTypes: EntityType[] = useMemo(() => {
        if (!entityTypes) return ['components', 'levels', 'rooms'];
        return Array.isArray(entityTypes) ? entityTypes : [entityTypes];
    }, [entityTypes]);

    // ── Fetch archive data ──────────────────────────────────────

    useEffect(() => {
        const loadHistory = async () => {
            if (!buildingKey) return;

            setLoading(true);
            setError(null);

            try {
                const db = getDatabase();
                const entries: HistoryEntry[] = [];

                const parseArchiveEntries = (
                    entryKey: string,
                    archive: Record<string, ArchiveEntry>,
                    entryEntityType: EntityType
                ) => {
                    for (const archiveKey of Object.keys(archive)) {
                        const entry = archive[archiveKey] as ArchiveEntry;
                        if (entry._diff && entry._diff.changes) {
                            const relevant = entry._diff.changes
                                .filter((c) => RELEVANT_FIELDS.has(c.field))
                                .map((c) => c.changedBy ? c : {
                                    ...c,
                                    changedBy: extractSnapshotUpdatedBy(
                                        (entry as unknown as Record<string, unknown>)[c.field],
                                    ),
                                });
                            if (relevant.length > 0) {
                                const entityName = getEntityDisplayName(entry);
                                entries.push({
                                    archiveKey,
                                    archivedAt: entry._archiveMetadata.archivedAt,
                                    entityType: entryEntityType,
                                    entityKey: entryKey,
                                    entityName,
                                    changes: relevant,
                                    // Backwards compatibility
                                    componentKey: entryKey,
                                    componentName: entityName,
                                });
                            }
                        }
                    }
                };

                // Load data for each entity type
                for (const type of normalizedTypes) {
                    const archivePath = entityKey
                        ? `archive/changeLog/${buildingKey}/${type}/${entityKey}`
                        : `archive/changeLog/${buildingKey}/${type}`;

                    const snapshot = await get(ref(db, archivePath));

                    if (snapshot.exists()) {
                        const archiveData = snapshot.val();

                        if (entityKey) {
                            parseArchiveEntries(entityKey, archiveData, type);
                        } else {
                            for (const key of Object.keys(archiveData)) {
                                parseArchiveEntries(key, archiveData[key], type);
                            }
                        }
                    }
                }

                entries.sort((a, b) => b.archivedAt.localeCompare(a.archivedAt));
                setHistory(entries);
            } catch (err) {
                console.error('Error loading history:', err);
                setError('Kunde inte ladda historik');
            } finally {
                setLoading(false);
            }
        };

        loadHistory();
    }, [buildingKey, entityKey, normalizedTypes]);

    // ── Grouped + filtered history (single useMemo) ─────────────

    const groupedHistory = useMemo<HistoryDateGroup[]>(() => {
        if (history.length === 0) return [];

        // 1. Filter by date range
        const hasStart = startDate !== '';
        const hasEnd = endDate !== '';

        // Handle invalid range
        if (hasStart && hasEnd && startDate > endDate) return [];

        const filtered = history.filter((entry) => {
            const date = extractDateKey(entry.archivedAt);
            if (hasStart && date < startDate) return false;
            if (hasEnd && date > endDate) return false;
            return true;
        });

        if (filtered.length === 0) return [];

        // 2. Group by day
        const groupMap: Record<string, HistoryEntry[]> = {};

        for (const entry of filtered) {
            const dateKey = extractDateKey(entry.archivedAt);
            if (!groupMap[dateKey]) {
                groupMap[dateKey] = [];
            }
            groupMap[dateKey].push(entry);
        }

        // 3. Convert to HistoryDateGroup[], sort by date descending
        const groups: HistoryDateGroup[] = Object.entries(groupMap).map(([dateKey, entries]) => ({
            groupKey: dateKey,
            dateKey,
            userUids: collectUserUids(entries),
            entries,
        }));

        groups.sort((a, b) => b.dateKey.localeCompare(a.dateKey));

        return groups;
    }, [history, startDate, endDate]);

    // ── Reset expanded state when groups change ─────────────────

    useEffect(() => {
        setExpandedGroups(new Set());
    }, [groupedHistory]);

    // ── Actions ─────────────────────────────────────────────────

    const toggleGroup = useCallback((groupKey: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    }, []);

    const clearDateFilter = useCallback(() => {
        setStartDate('');
        setEndDate('');
    }, []);

    // ── Inline-computable values (no memo needed) ───────────────

    const totalChanges = history.reduce((n, e) => n + e.changes.length, 0);
    const filteredChanges = groupedHistory.reduce(
        (n, g) => n + g.entries.reduce((m, e) => m + e.changes.length, 0),
        0,
    );

    return {
        loading,
        error,
        history,
        groupedHistory,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        clearDateFilter,
        expandedGroups: expandedGroups as ReadonlySet<string>,
        toggleGroup,
        totalChanges,
        filteredChanges,
    };
}
